import type { ContactType, DistrictView, RelationKind } from '@erp/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from './api.ts';

// Types mirror the API's responses (apps/api/src/contacts/contacts.types.ts).

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface ContactSummary {
  id: string;
  kind: 'person' | 'company';
  name: string;
  phone: string | null;
  email: string | null;
  types: ContactType[];
  tags: Tag[];
  assignedTo: { membershipId: string; name: string };
  channel: { id: string; name: string } | null;
  district: { id: number; name: string; municipality: string } | null;
  createdAt: string;
  flags: {
    rectification: boolean;
    limited: boolean;
    marketingOptOut: boolean;
    noticePending: boolean;
  };
}

export interface ContactDetail extends ContactSummary {
  phones: { number: string; label: 'mobile' | 'home' | 'work' | 'other'; whatsapp: boolean }[];
  emails: { address: string; label: 'personal' | 'work' | 'other' }[];
  relations: {
    id: string;
    kind: RelationKind;
    outgoing: boolean;
    contact: { id: string; name: string };
  }[];
  privateNotes: string | null;
  hasNationalId: boolean;
  hasAddress: boolean;
  createdBy: string | null;
  updatedAt: string;
  privacy: {
    rectification: { since: string; note: string | null } | null;
    limited: { since: string; note: string | null } | null;
    marketingOptOut: string | null;
    /** Imported: since when the privacy notice is pending. */
    noticePending: string | null;
    /** Anonymized at the holder's request (irreversible). */
    anonymizedAt: string | null;
  };
  can: { edit: boolean; delete: boolean; revealSensitive: boolean };
}

export interface DuplicateMatch {
  matchedBy: ('phone' | 'email' | 'nationalId')[];
  contact: { id: string; name: string } | null;
  assignedTo: string;
}

export interface ContactFilters {
  q?: string;
  type?: ContactType;
  tagId?: string;
  channelId?: string;
  assignedMembershipId?: string;
  createdFrom?: string;
  createdTo?: string;
  noticePending?: 'true';
  page?: number;
}

export const contactKeys = {
  all: ['contacts'] as const,
  list: (filters: ContactFilters) => ['contacts', 'list', filters] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
  trash: ['contacts', 'trash'] as const,
  tags: ['tags'] as const,
  channels: ['channels'] as const,
  assignees: ['contacts', 'assignees'] as const,
};

function toQuery(filters: ContactFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const useContacts = (filters: ContactFilters, enabled = true) =>
  useQuery({
    queryKey: contactKeys.list(filters),
    queryFn: () => api<{ total: number; items: ContactSummary[] }>(`/contacts${toQuery(filters)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useContact = (id: string, enabled = true) =>
  useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => api<ContactDetail>(`/contacts/${id}`),
    enabled,
    retry: false,
  });

export const useTags = () =>
  useQuery({ queryKey: contactKeys.tags, queryFn: () => api<Tag[]>('/tags') });

export const useChannels = () =>
  useQuery({
    queryKey: contactKeys.channels,
    queryFn: () => api<{ id: string; name: string; active: boolean }[]>('/channels'),
  });

export const useAssignees = () =>
  useQuery({
    queryKey: contactKeys.assignees,
    queryFn: () => api<{ membershipId: string; name: string }[]>('/contacts/assignees'),
    staleTime: 5 * 60_000,
  });

export const useDistrictSearch = (q: string) =>
  useQuery({
    queryKey: ['districts', q],
    queryFn: () => api<DistrictView[]>(`/locations/districts?q=${encodeURIComponent(q)}&limit=10`),
    enabled: q.trim().length > 0,
    staleTime: 60 * 60_000,
    placeholderData: keepPreviousData,
  });

/** "https://wa.me/50371234567": opens WhatsApp (app or web) with that number. */
export const whatsappLink = (e164: string, text?: string) =>
  `https://wa.me/${e164.replace(/\D/g, '')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
